import React, { useState } from 'react'
import styled from 'styled-components'
import { useQuery, UseQueryResult } from 'react-query'

import { Helmet } from 'react-helmet'
import { NavLink as RouterNavLink, NavLinkProps, useParams } from 'react-router-dom'
import {
  Breadcrumbs as MuiBreadcrumbs,
  Card as MuiCard,
  CardContent,
  Chip,
  Divider as MuiDivider,
  Grid,
  IconButton,
  Link,
  Menu,
  MenuItem,
  Typography,
} from '@material-ui/core'
import { spacing } from '@material-ui/system'
import { Home as HomeIcon, Print as PrintIcon } from '@material-ui/icons'

import {
  useQueryPaginator,
  AppointmentField,
  DetailTable,
  LinkedField,
  PaginatedTable,
  RelatedLinkedField,
  RelatedListValueField,
  ValueField,
  StatusField,
} from 'src/components/Table'

import { skuit } from 'src/services/skuit'
import { Truckload, Tag } from 'src/api/models'
import { ExportProps } from 'src/services/skuit/types'
import { queryClient } from 'src/App'
import { useAsyncPrint } from 'src/components/ToPrint/useAsyncPrint'
import { TruckloadShippingManifest } from 'src/modules/client/components/Print'
import { AddAttachmentsDialog } from 'src/components/Dialog/AddAttachmentsDialog'
import { GenericPreview, PaginatedFilePreviewGallery } from 'src/components/FilePreview'

const NavLink = React.forwardRef((props: NavLinkProps, ref: React.Ref<HTMLAnchorElement>) => (
  <RouterNavLink innerRef={ref} {...props} />
))
NavLink.displayName = 'NavLink'

const Card = styled(MuiCard)(spacing)

const Divider = styled(MuiDivider)(spacing)

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing)

const Centered = styled.div`
  text-align: center;
`
interface DetailsProps {
  truckload: UseQueryResult<Truckload>
  refresh: () => void
}

const Details = (props: DetailsProps) => {
  const { truckload } = props
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const { print, renderPrintComponent } = useAsyncPrint()

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }
  const handleClose = () => {
    setAnchorEl(null)
  }

  return (
    <Card mb={6}>
      {renderPrintComponent()}
      <CardContent>
        <Centered>
          <HomeIcon />
          <Typography variant="h6" gutterBottom>
            {truckload && truckload.data?.name}
          </Typography>
          <Grid container justify="center" spacing={4}>
            {truckload.data?.status === 'shipped' && (
              <Grid item>
                <IconButton size="small" onClick={handleClick}>
                  <PrintIcon fontSize="small" />
                </IconButton>
                <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
                  <MenuItem
                    onClick={() => {
                      print((props) => (
                        <TruckloadShippingManifest
                          {...props}
                          truckload={truckload.data as Truckload}
                        />
                      ))
                      handleClose()
                    }}
                  >
                    Print Shipping Manifest
                  </MenuItem>
                </Menu>
              </Grid>
            )}
          </Grid>
          <DetailTable
            obj={truckload}
            fields={[
              { id: 'guid', label: 'SquID', renderer: ValueField },
              { id: 'trackingNumber', label: 'Tracking Number', renderer: ValueField },
              { id: 'billOfLadingNumber', label: 'Bill of Lading Number', renderer: ValueField },
              { id: 'trailerNumber', label: 'Trailer Number', renderer: ValueField },
              {
                id: 'facility',
                label: 'Facility',
                renderer: RelatedLinkedField(ValueField, 'name'),
              },
              { id: 'carrier', label: 'Carrier', renderer: RelatedLinkedField(ValueField, 'name') },
              { id: 'status', label: 'Status', renderer: StatusField },
              { id: 'appointment', label: 'Appointment', renderer: AppointmentField },
            ]}
          />
        </Centered>
      </CardContent>
    </Card>
  )
}

interface PathParams {
  guid: string
}

export const TruckloadPage = () => {
  const { guid } = useParams<PathParams>()
  const [addAttachmentDialogOpen, setAddAttachmentDialogOpen] = useState(false)
  const truckload = useQuery<Truckload>(['truckload-details', guid], () =>
    skuit.client.freight.truckloads.retrieve(guid)
  )

  const attachmentsPaginator = useQueryPaginator(
    'truckload-attachments',
    () => skuit.client.freight.truckloads.attachments.list(guid),
    {
      params: { page_size: 4, orders: guid },
    }
  )
  const shipmentPaginator = useQueryPaginator(
    'truckload-shipment-list',
    skuit.client.freight.shipments.list,
    { params: { page_size: 5, truckloads: guid } }
  )
  const unitsPaginator = useQueryPaginator(
    'truckload-unit-list',
    skuit.client.freight.shipments.units.list,
    { params: { page_size: 5, truckloads: guid } }
  )
  const barcodedPaginator = useQueryPaginator(
    'truckload-barcoded-unit-list',
    skuit.client.freight.shipments.barcodedUnits.list,
    { params: { page_size: 5, truckloads: guid } }
  )
  const palletsPaginator = useQueryPaginator(
    'truckload-pallet-list',
    skuit.client.freight.shipments.pallets.list,
    { params: { page_size: 5, truckloads: guid } }
  )

  const refresh = () => {
    queryClient.invalidateQueries(['truckload-details'])
    attachmentsPaginator.refresh()
    barcodedPaginator.refresh()
    palletsPaginator.refresh()
    shipmentPaginator.refresh()
    unitsPaginator.refresh()
  }

  return (
    <>
      <Helmet title="Truckload" />
      {addAttachmentDialogOpen && (
        <AddAttachmentsDialog
          onSuccess={refresh}
          addAttachment={(config) =>
            skuit.client.freight.truckloads.attachments.create(guid, config?.data, config)
          }
          open={addAttachmentDialogOpen}
          handleClose={() => {
            setAddAttachmentDialogOpen(false)
          }}
        />
      )}
      <Grid justify="space-between" container spacing={10}>
        <Grid item>
          <Typography variant="h3" gutterBottom display="inline">
            Truckload
          </Typography>
          <Breadcrumbs aria-label="Breadcrumb" mt={2}>
            <Link component={NavLink} exact to="/">
              Dashboard
            </Link>
            <Link component={NavLink} exact to="/freight/truckloads">
              Truckloads
            </Link>
            <Typography>{truckload && truckload.data?.name}</Typography>
          </Breadcrumbs>
        </Grid>
      </Grid>

      <Divider my={6} />

      <Grid container spacing={6}>
        {truckload && (
          <Grid item xs={12}>
            <Grid container spacing={6}>
              <Grid item xs={12} lg={6}>
                <Details truckload={truckload} refresh={refresh} />
              </Grid>
            </Grid>
          </Grid>
        )}
        <Grid item xs={12}>
          <PaginatedFilePreviewGallery
            title="Attachments"
            paginator={attachmentsPaginator}
            getIdent={(item) => item.guid}
            onAdd={() => {
              setAddAttachmentDialogOpen(true)
            }}
            renderItem={(item) => {
              return (
                <>
                  <GenericPreview url={item.file} />
                  <div>
                    {item.tags.map((tag: Tag) => (
                      <Chip key={tag.slug} label={tag.name} />
                    ))}
                  </div>
                </>
              )
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <PaginatedTable
            title="Shipments"
            fields={[
              { id: 'trackingNumber', label: 'Tracking Number', renderer: LinkedField(ValueField) },
              { id: 'order', label: 'Order', renderer: RelatedLinkedField(ValueField, 'number') },
              { id: 'consignee', label: 'Consignee', renderer: ValueField },
              { id: 'status', label: 'Status', renderer: StatusField },
            ]}
            paginator={shipmentPaginator}
          />
        </Grid>
        <Grid item xs={12}>
          <PaginatedTable
            title="Units"
            fields={[
              { id: 'sku.productName', label: 'Product Name', renderer: ValueField },
              { id: 'product', label: 'Product', renderer: RelatedLinkedField(ValueField, 'name') },
              { id: 'sku', label: 'Sku', renderer: RelatedLinkedField(ValueField, 'sku') },
              { id: 'numUnits', label: 'Number of Units', renderer: ValueField },
              {
                id: 'shipment.order',
                label: 'Order',
                renderer: RelatedLinkedField(ValueField, 'number'),
              },
              {
                id: 'shipment',
                label: 'Shipment',
                renderer: RelatedLinkedField(ValueField, 'name'),
              },
              {
                id: 'container',
                label: 'Container',
                renderer: RelatedLinkedField(ValueField, 'name'),
              },
            ]}
            paginator={unitsPaginator}
            exportConfig={{
              exportFields: [
                'truckload_guid',
                'truckload_tracking_number',
                'truckload_bill_of_lading_number',
                'truckload_trailer_number',
                'order_guid',
                'order_number',
                'shipment_guid',
                'shipment_tracking_number',
                'sku',
                'product',
                'num_units',
                'pallet',
                'client_pallet_id',
              ],
              exportAPI: (props: ExportProps) =>
                skuit.client.freight.shipments.units.export(props, { truckloads: guid }),
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <PaginatedTable
            title="Units (Barcoded)"
            fields={[
              { id: 'unit', label: 'Name', renderer: RelatedLinkedField(ValueField, 'name') },
              {
                id: 'unit.product',
                label: 'Product',
                renderer: RelatedLinkedField(ValueField, 'name'),
              },
              {
                id: 'shipment',
                label: 'Shipment',
                renderer: RelatedLinkedField(ValueField, 'name'),
              },
            ]}
            paginator={barcodedPaginator}
          />
        </Grid>
        <Grid item xs={12}>
          <PaginatedTable
            title="Pallets"
            fields={[
              { id: 'pallet', label: 'Pallet', renderer: RelatedLinkedField(ValueField, 'name') },
              { id: 'pallet.client.name', label: 'Client', renderer: ValueField },
              { id: 'pallet.clientID', label: 'Client ID', renderer: ValueField },
              { id: 'pallet.skus', label: 'SKUs', renderer: RelatedListValueField('sku') },
              { id: 'pallet.path', label: 'Path', renderer: ValueField },
              {
                id: 'shipment',
                label: 'Shipment',
                renderer: RelatedLinkedField(ValueField, 'name'),
              },
            ]}
            paginator={palletsPaginator}
          />
        </Grid>
      </Grid>
    </>
  )
}
